import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { MessageDto, ConversationDto } from '../../services/api/messageService';
import { messageService } from '../../services/api/messageService';
import { chatSignalRService } from '../../services/signalr/ChatSignalRService';
import { BlockReportMenu, type ReportReason } from '../common/BlockReportMenu';
import { ProfileCardTrigger } from '../profile/ProfileCardTrigger';
import './ChatWindow.css';

interface ChatWindowProps {
  conversation: ConversationDto;
  onBack?: () => void;
  onUnmatch?: (matchId: string) => Promise<void>;
  onBlock?: (profileId: string) => Promise<void>;
  onReport?: (profileId: string, reason: ReportReason, details?: string) => Promise<void>;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
  conversation,
  onBack,
  onUnmatch,
  onBlock,
  onReport,
}) => {
  const [messages, setMessages] = useState<MessageDto[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Load messages
  const loadMessages = useCallback(async (before?: string) => {
    try {
      const response = await messageService.getMessages(conversation.matchId, 50, before);
      if (before) {
        setMessages(prev => [...response.messages, ...prev]);
      } else {
        setMessages(response.messages);
      }
      setHasMore(response.hasMore);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setIsLoading(false);
    }
  }, [conversation.matchId]);

  // Initial load and SignalR setup
  useEffect(() => {
    setIsLoading(true);
    loadMessages();

    // Join the match room for real-time updates
    chatSignalRService.joinMatch(conversation.matchId);

    // Mark messages as read
    messageService.markAsRead(conversation.matchId);

    return () => {
      chatSignalRService.leaveMatch(conversation.matchId);
    };
  }, [conversation.matchId, loadMessages]);

  // Set up SignalR event handlers
  useEffect(() => {
    chatSignalRService.setEventHandlers({
      onReceiveMessage: (message) => {
        if (message.matchId === conversation.matchId) {
          setMessages(prev => [...prev, message]);
          // Mark as read since we're viewing this conversation
          messageService.markAsRead(conversation.matchId);
          setOtherTyping(false);
        }
      },
      onTypingIndicator: (indicator) => {
        if (indicator.matchId === conversation.matchId &&
            indicator.profileId !== conversation.otherProfileId) {
          setOtherTyping(indicator.isTyping);
        }
      },
      onMessagesRead: (event) => {
        if (event.matchId === conversation.matchId) {
          // Update read status on our messages
          setMessages(prev => prev.map(msg =>
            msg.isOwnMessage ? { ...msg, readAt: event.readAt } : msg
          ));
        }
      },
    });
  }, [conversation.matchId, conversation.otherProfileId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (!isLoading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  // Handle typing indicator
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);

    // Send typing indicator
    if (!isTyping) {
      setIsTyping(true);
      chatSignalRService.sendTypingIndicator(conversation.matchId, true);
    }

    // Clear previous timeout and set new one
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      chatSignalRService.sendTypingIndicator(conversation.matchId, false);
    }, 2000);
  };

  // Send message
  const handleSend = async () => {
    if (!newMessage.trim() || isSending) return;

    const content = newMessage.trim();
    setNewMessage('');
    setIsSending(true);
    setIsTyping(false);
    chatSignalRService.sendTypingIndicator(conversation.matchId, false);

    try {
      // Try SignalR first for immediate delivery
      if (chatSignalRService.isConnected) {
        const result = await chatSignalRService.sendMessage(conversation.matchId, content);
        if (!result.success) {
          // Fall back to REST API
          await messageService.sendMessage({ matchId: conversation.matchId, content });
        }
      } else {
        // Use REST API
        const result = await messageService.sendMessage({ matchId: conversation.matchId, content });
        if (result.success && result.message) {
          setMessages(prev => [...prev, result.message!]);
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // TODO: Show error toast
    } finally {
      setIsSending(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Load more messages
  const handleLoadMore = () => {
    if (messages.length > 0 && hasMore) {
      loadMessages(messages[0].sentAt);
    }
  };

  // Format timestamp
  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if we should show date separator
  const shouldShowDateSeparator = (current: MessageDto, previous?: MessageDto) => {
    if (!previous) return true;
    const currentDate = new Date(current.sentAt).toDateString();
    const previousDate = new Date(previous.sentAt).toDateString();
    return currentDate !== previousDate;
  };

  const formatDateSeparator = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString([], {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  return (
    <div className="chat-window">
      {/* Header */}
      <div className="chat-header">
        {onBack && (
          <button className="back-button" onClick={onBack}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
          </button>
        )}
        <ProfileCardTrigger profileId={conversation.otherProfileId}>
          <div className="chat-header-avatar">
            {conversation.otherPhotoUrl ? (
              <img src={conversation.otherPhotoUrl} alt={conversation.otherDisplayName} />
            ) : (
              <div className="avatar-placeholder">
                {conversation.otherDisplayName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        </ProfileCardTrigger>
        <div className="chat-header-info">
          <span className="chat-header-name">
            {conversation.otherDisplayName}
            {conversation.otherIsVerified && (
              <svg viewBox="0 0 24 24" fill="currentColor" className="verified-icon">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
            )}
          </span>
          {otherTyping && <span className="typing-status">typing...</span>}
        </div>
        <BlockReportMenu
          profileId={conversation.otherProfileId}
          profileName={conversation.otherDisplayName}
          onBlock={onBlock || (async () => { console.warn('Block handler not provided'); })}
          onReport={onReport || (async () => { console.warn('Report handler not provided'); })}
          onUnmatch={onUnmatch ? () => onUnmatch(conversation.matchId) : undefined}
          showUnmatch={!!onUnmatch}
          className="chat-header-menu"
        />
      </div>

      {/* Messages */}
      <div className="chat-messages" ref={messagesContainerRef}>
        {hasMore && (
          <button className="load-more-button" onClick={handleLoadMore}>
            Load earlier messages
          </button>
        )}

        {isLoading ? (
          <div className="chat-loading">
            <div className="spinner"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="chat-empty">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message, index) => (
            <React.Fragment key={message.id}>
              {shouldShowDateSeparator(message, messages[index - 1]) && (
                <div className="date-separator">
                  <span>{formatDateSeparator(message.sentAt)}</span>
                </div>
              )}
              <div
                className={`message ${message.isOwnMessage ? 'own' : 'other'}`}
              >
                {!message.isOwnMessage && (
                  <div className="message-avatar">
                    {conversation.otherPhotoUrl ? (
                      <img src={conversation.otherPhotoUrl} alt="" />
                    ) : (
                      <div className="avatar-placeholder-small">
                        {conversation.otherDisplayName.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                )}
                <div className="message-bubble">
                  {message.replyToMessage && (
                    <div className="reply-preview">
                      <span className="reply-name">
                        {message.replyToMessage.isOwnMessage ? 'You' : conversation.otherDisplayName}
                      </span>
                      <span className="reply-content">{message.replyToMessage.content}</span>
                    </div>
                  )}
                  {message.type === 'image' && message.attachmentUrl && (
                    <img
                      src={message.attachmentUrl}
                      alt="Shared image"
                      className="message-image"
                    />
                  )}
                  {message.type === 'text' && (
                    <p className="message-content">{message.content}</p>
                  )}
                  <div className="message-meta">
                    <span className="message-time">{formatTime(message.sentAt)}</span>
                    {message.isOwnMessage && (
                      <span className="message-status">
                        {message.readAt ? (
                          <svg viewBox="0 0 24 24" fill="currentColor" className="read">
                            <path d="M18 7l-1.41-1.41-6.34 6.34 1.41 1.41L18 7zm4.24-1.41L11.66 16.17 7.48 12l-1.41 1.41L11.66 19l12-12-1.42-1.41zM.41 13.41L6 19l1.41-1.41L1.83 12 .41 13.41z" />
                          </svg>
                        ) : message.deliveredAt ? (
                          <svg viewBox="0 0 24 24" fill="currentColor" className="delivered">
                            <path d="M18 7l-1.41-1.41-6.34 6.34 1.41 1.41L18 7zm4.24-1.41L11.66 16.17 7.48 12l-1.41 1.41L11.66 19l12-12-1.42-1.41z" />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="currentColor" className="sent">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                          </svg>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </React.Fragment>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="chat-input">
        <input
          type="text"
          placeholder="Type a message..."
          value={newMessage}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          disabled={isSending}
        />
        <button
          className="send-button"
          onClick={handleSend}
          disabled={!newMessage.trim() || isSending}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ChatWindow;
